﻿import { Dispatch } from 'redux';

import { ComplianceAutotrackingRequest } from '../../model/compliance/ComplianceAutotrackingRequest';
import { CompliancePeriod } from '../../model/compliance/CompliancePeriod';
import complianceService from '../../services/ComplianceService';
import {
  CLEAR_COMPLIANCE,
  FETCH_COMPLIANCE_OVERVIEW,
  FETCH_COMPLIANCE_OVERVIEW_FAILURE,
  FETCH_COMPLIANCE_OVERVIEW_SUCCESS,
  FETCH_COMPLIANCE_PERIODS,
  FETCH_COMPLIANCE_PERIODS_FAILURE,
  FETCH_COMPLIANCE_PERIODS_SUCCESS,
  TOGGLE_COMPLIANCE_AUTOTRACKING,
  TOGGLE_COMPLIANCE_AUTOTRACKING_FAILURE,
  TOGGLE_COMPLIANCE_AUTOTRACKING_SUCCESS,
  UPDATE_COMPLIANCE_PERIOD,
  UPDATE_COMPLIANCE_PERIOD_FAILURE,
  UPDATE_COMPLIANCE_PERIOD_SUCCESS,
} from '../constants';
import { ComplianceActionTypes } from '../types/complianceActionTypes';

export function fetchComplianceOverview(): ComplianceActionTypes {
  return {
    type: FETCH_COMPLIANCE_OVERVIEW,
  };
}

export function fetchComplianceOverviewSuccess(data: any) {
  return {
    type: FETCH_COMPLIANCE_OVERVIEW_SUCCESS,
    payload: data,
  };
}

export function fetchComplianceOverviewError(
  error: string
): ComplianceActionTypes {
  return {
    type: FETCH_COMPLIANCE_OVERVIEW_FAILURE,
    payload: error,
  };
}

export function getComplianceOverview(patientGuid: string) {
  return function action(dispatch: Dispatch<any>) {
    dispatch(fetchComplianceOverview());
    return complianceService.getOverview(patientGuid).then(
      (response: any) => dispatch(fetchComplianceOverviewSuccess(response)),
      (error: any) =>
        dispatch(
          fetchComplianceOverviewError(
            error?.message || 'Failed to load compliance overview'
          )
        )
    );
  };
}

export function fetchCompliancePeriods(): ComplianceActionTypes {
  return {
    type: FETCH_COMPLIANCE_PERIODS,
  };
}

export function fetchCompliancePeriodsSuccess(data: any) {
  return {
    type: FETCH_COMPLIANCE_PERIODS_SUCCESS,
    payload: data,
  };
}

export function fetchCompliancePeriodsError(
  error: string
): ComplianceActionTypes {
  return {
    type: FETCH_COMPLIANCE_PERIODS_FAILURE,
    payload: error,
  };
}

export function getCompliancePeriods(patientGuid: string) {
  return function action(dispatch: Dispatch<any>) {
    dispatch(fetchCompliancePeriods());
    return complianceService.getPeriods(patientGuid).then(
      (response: any) => dispatch(fetchCompliancePeriodsSuccess(response)),
      (error: any) =>
        dispatch(
          fetchCompliancePeriodsError(
            error?.message || 'Failed to load compliance periods'
          )
        )
    );
  };
}

export function updateCompliancePeriod(
  data: CompliancePeriod
): ComplianceActionTypes {
  return {
    type: UPDATE_COMPLIANCE_PERIOD,
    payload: data,
  };
}

export function updateCompliancePeriodSuccess(data: any) {
  return {
    type: UPDATE_COMPLIANCE_PERIOD_SUCCESS,
    payload: data,
  };
}

export function updateCompliancePeriodError(
  error: string
): ComplianceActionTypes {
  return {
    type: UPDATE_COMPLIANCE_PERIOD_FAILURE,
    payload: error,
  };
}

export function updatePatientCompliancePeriod(period: CompliancePeriod) {
  return function action(dispatch: Dispatch<any>) {
    dispatch(updateCompliancePeriod(period));
    return complianceService.updatePeriod(period).then(
      (response: any) => dispatch(updateCompliancePeriodSuccess(response)),
      (error: any) =>
        dispatch(
          updateCompliancePeriodError(
            error?.message || 'Failed to update compliance period'
          )
        )
    );
  };
}

export function toggleComplianceAutotracking(): ComplianceActionTypes {
  return {
    type: TOGGLE_COMPLIANCE_AUTOTRACKING,
  };
}

export function toggleComplianceAutotrackingSuccess(data: any) {
  return {
    type: TOGGLE_COMPLIANCE_AUTOTRACKING_SUCCESS,
    payload: data,
  };
}

export function toggleComplianceAutotrackingError(
  error: string
): ComplianceActionTypes {
  return {
    type: TOGGLE_COMPLIANCE_AUTOTRACKING_FAILURE,
    payload: error,
  };
}

export function togglePatientComplianceAutotracking(
  accountId: string,
  isEnabled: boolean
) {
  return async function action(dispatch: Dispatch<any>) {
    dispatch(toggleComplianceAutotracking());
    const data: ComplianceAutotrackingRequest = {
      patientId: accountId,
      isEnabled: isEnabled,
    };

    return complianceService.toggleAutoTracking(data).then(
      (response: any) => {
        dispatch(toggleComplianceAutotrackingSuccess(data.isEnabled));
      },
      (error: any) =>
        dispatch(
          toggleComplianceAutotrackingError(
            error?.message || 'Failed to toggle compliance autotracking'
          )
        )
    );
  };
}

export function clearComplianceData(): ComplianceActionTypes {
  return {
    type: CLEAR_COMPLIANCE,
  };
}
